import { defineStore } from "pinia";
import { useStorage } from "@vueuse/core";
import { computed } from "vue";
interface SpecValue {
    spec_value_id: number;
    spec_value: string;
    spec_group_id: number;
}
interface ProductComparison {
    group: string;
    value: SpecValue | null;
}

interface ProductComparisons {
    [key: string]: ProductComparison[];
}
export const useCompareDeckStore = defineStore('compareDeck', ()=>{
    let products = useStorage('compareDeck-products', []);

    let colorSwatches = useStorage('compareDeck-colorSwatches', {});

    let allSpecGroups = computed(()=>{
        const specs = new Set();
        products.value.forEach((product)=>{
            product.item_specifications.forEach((spec)=>{
                specs.add(spec.group.spec_group_name);
            });
        });
        return Array.from(specs);
    })

    let productCount = computed(()=>{
        return products.length;
    })

    let productSelectedVariants = computed(()=>{
        return products.value.reduce((acc,product)=>{
            if(!acc.has(product.item_name)){
                acc.set(product.item_name, new Map());
            }
            for(const variant of product.variation_specifications){
                const selectedVariantName = `Selected ${variant.group.spec_group_name}`;
                const selectedVariantValue = variant.value.spec_value;
                let map = acc.get(product.item_name);
                if(!map.has(selectedVariantName)){
                    map.set(selectedVariantName, new Set());
                }
                map.get(selectedVariantName).add(selectedVariantValue);
            }
            return acc;
        },new Map());
    })

    let productSpecs = computed(()=>{
        return products.value.reduce((acc, product)=>{
            if(!acc.has(product.item_name)){
                acc.set(product.item_name, new Map());
            }
            for(let spec of allSpecGroups.value){
                const productSpecs = product.item_specifications.filter(s=>s.group.spec_group_name === spec).sort((a, b)=>a.value.sort_order - b.value.sort_order);
                let map = acc.get(product.item_name);
                if(!map.has(spec)){
                    map.set(spec, new Set());
                }
                productSpecs.forEach(s=>{
                    map.get(spec).add(s.value.spec_value);
                });
            }
            return acc;
        }, new Map());
    })

    const productDifferences = computed(()=>{
        const productMaps = productSpecs.value;
        const productVariantMaps = productSelectedVariants.value;
        const diffs = new Map();
        if(products.value.length < 2){
            return diffs;
        }

        for(const [product, specs] of productMaps){
            const productDiffs = new Map();
            for(const [group, values] of specs){
                let otherProductSpecs = new Set();
                productMaps.forEach((value, key)=>{
                    if(key !== product){
                        let pSpecs = value.get(group);
                        if(pSpecs){
                            otherProductSpecs = otherProductSpecs.union(pSpecs);
                        }
                    }
                });
                const differences = values.difference(otherProductSpecs);
                productDiffs.set(group, differences);
            }
            diffs.set(product, productDiffs);
        }

        for(const [product, variants] of productVariantMaps){
            const variantDiffs = new Map();
            for(const [group, values] of variants){
                let otherProductVariants = new Set();
                productVariantMaps.forEach((value, key)=>{
                    if(key !== product){
                        const pVariants = value.get(group);
                        if(pVariants){
                            otherProductVariants = otherProductVariants.union(pVariants);
                        }
                    }
                });
                const differences = values.difference(otherProductVariants);
                variantDiffs.set(group, differences);
            }
            diffs.set(product, new Map([...diffs.get(product),...variantDiffs]));
        }

        return diffs;
    })

    const productSimilarities = computed(()=>{
        const productMaps = productSpecs.value;
        const productVariantMaps = productSelectedVariants.value;
        const similarities = new Map();
        if(products.value.length < 2){
            return similarities;
        }

        for(const [product, specs] of productMaps){
            const productSimilarities = new Map();
            for(const [group, values] of specs){
                let otherProductSpecs = new Set();
                productMaps.forEach((value, key)=>{
                    if(key !== product){
                        const pSpecs = value.get(group);
                        if(pSpecs){
                            otherProductSpecs = otherProductSpecs.union(pSpecs);
                        }
                    }
                });
                const similarities = values.intersection(otherProductSpecs);
                productSimilarities.set(group, similarities);
            }
            similarities.set(product, productSimilarities);
        }

        for(const [product, variants] of productVariantMaps){
            const variantSimilarities = new Map();
            for(const [group, values] of variants){
                let otherProductVariants = new Set();
                productVariantMaps.forEach((value, key)=>{
                    if(key !== product){
                        const pVariants = value.get(group);
                        if(pVariants){
                            otherProductVariants = otherProductVariants.union(pVariants);
                        }
                    }
                });
                const similarities = values.intersection(otherProductVariants);
                variantSimilarities.set(group, similarities);
            }
            similarities.set(product, new Map([...similarities.get(product),...variantSimilarities]));
        }

        return similarities;
    })

    let getPrimaryImage = (item_name: string)=>{
        const product = products.value.find(p=>p.item_name === item_name);
        return product ? product.primary_image : null;
    }

    const setProducts = (products)=>{
        products.value = products;
    }

    const addProductToDeck = ({selectedVariant, item_specifications, pricing, item_name, primary_image, swatch_images})=>{
        if(products.value.length < 4){
            let itemFound = products.value.find(p=>p.item_variation_id === selectedVariant.item_variation_id || p.item_index === selectedVariant.item_index || p.item_name === item_name);
            console.log(swatch_images);

            if(!itemFound){
                item_specifications = item_specifications ? item_specifications.sort((a, b)=>a.group.sort_order - b.group.sort_order) : [];
                products.value.push({
                    item_name,
                    ...selectedVariant,
                    primary_image,
                    item_specifications,
                    pricing
                });

                if(swatch_images){
                    for(let color in swatch_images){
                        const image = swatch_images[color];
                        if(!colorSwatches.value[color]){
                            colorSwatches.value[color] = image;
                        }
                    }
                }
            }
        }
    }

    function removeProductFromDeck(product){
        const index = products.value.findIndex(p=>p.item_variation_id === product.item_variation_id || p.item_index === product.item_index || p.item_name === product.item_name);
        if(index > -1){
            products.value.splice(index, 1);
        }
    }

    const clearDeck = ()=>{
        products.value = [];
        colorSwatches.value = {};
    }

    return {
        colorSwatches,
        products,
        allSpecGroups,
        productCount,
        productSelectedVariants,
        productSpecs,
        productDifferences,
        productSimilarities,
        getPrimaryImage,
        setProducts,
        addProductToDeck,
        removeProductFromDeck,
        clearDeck
    }
})
// export const useCompareDeckStore = defineStore("compareDeck", {
//     state: () => ({
//         products: useStorage("compareDeck-products", [])
//     }),
//     getters: {
//         allSpecGroups(state) {
//             const specs = new Set();
//             state.products.forEach((product) => {
//                 product.item_specifications.forEach((spec) => {
//                     specs.add(spec.group.spec_group_name);
//                 });
//             });
//             return Array.from(specs);
//         },
//         productCount() {
//             return this.products.length;
//         },
//         productSpecs() {
//             return this.products.reduce((acc, product) => {
//                 if (!acc.has(product.item_name)) {
//                     acc.set(product.item_name, new Map());
//                 }
//                 for (let spec of this.allSpecGroups) {
//                     const productSpecs = product.item_specifications
//                         .filter((s) => s.group.spec_group_name === spec)
//                         .sort(
//                             (a, b) => a.value.sort_order - b.value.sort_order
//                         );

//                     let map = acc.get(product.item_name);
//                     if (!map.has(spec)) {
//                         map.set(spec, new Set());
//                     }
//                     productSpecs.forEach((s) => {
//                         map.get(spec).add(s.value.spec_value);
//                     });
//                 }

//                 return acc;
//             }, new Map());
//         },

//         productDifferences() {
//             const productMaps = this.productSpecs;
//             const diffs = new Map();
//             if (this.products.length < 2) {
//                 return diffs;
//             }

//             for (const [product, specs] of productMaps) {
//                 const productDiffs = new Map();
//                 for (const [group, values] of specs) {
//                     let otherProductSpecs = new Set();
//                     productMaps.forEach((value, key) => {
//                         if (key !== product) {
//                             let pSpecs = value.get(group);
//                             if (pSpecs) {
//                                 otherProductSpecs =
//                                     otherProductSpecs.union(pSpecs);
//                             }
//                         }
//                     });
//                     //get differences between all products against current product
//                     const differences = values.difference(otherProductSpecs);
//                     productDiffs.set(group, differences);
//                 }
//                 diffs.set(product, productDiffs);
//             }
//             return diffs;
//         },
//         productSimilarities() {
//             const productMaps = this.productSpecs;
//             const similarities = new Map();
//             if (this.products.length < 2) {
//                 return similarities;
//             }
//             // should do differences between the products spec groups
//             for (const [product, specs] of productMaps) {
//                 const productSimilarities = new Map();

//                 for (const [group, values] of specs) {
//                     let otherProductSpecs = new Set();
//                     productMaps.forEach((value, key) => {
//                         if (key !== product) {
//                             const pSpecs = value.get(group);
//                             if (pSpecs) {
//                                 otherProductSpecs =
//                                     otherProductSpecs.union(pSpecs);
//                             }
//                         }
//                     });
//                     const similarities = values.intersection(otherProductSpecs);
//                     productSimilarities.set(group, similarities);
//                 }
//                 similarities.set(product, productSimilarities);
//             }
//             return similarities;
//         }
//     },

//     actions: {
//         getPrimaryImage(item_name) {
//             const product = this.products.find(
//                 (p) => p.item_name === item_name
//             );
//             return product ? product.primary_image : null;
//         },
//         setProducts(products) {
//             this.products = products;
//         },
//         addProductToDeck({
//             selectedVariant,
//             item_specifications,
//             pricing,
//             item_name,
//             primary_image
//         }) {
//             if (this.products.length < 4) {
//                 let itemFound = this.products.find(
//                     (p) =>
//                         p.item_variation_id ===
//                             selectedVariant.item_variation_id ||
//                         p.item_index === selectedVariant.item_index ||
//                         p.item_name === item_name
//                 );
//                 if (!itemFound) {
//                     item_specifications = item_specifications
//                         ? item_specifications.sort(
//                               (a, b) => a.group.sort_order - b.group.sort_order
//                           )
//                         : [];

//                     this.products.push({
//                         item_name,
//                         ...selectedVariant,
//                         primary_image,
//                         item_specifications,
//                         pricing
//                     });
//                 }
//             }
//         },

//         removeProductFromDeck(product) {
//             const index = this.products.findIndex(
//                 (p) =>
//                     p.item_variation_id === product.item_variation_id ||
//                     p.item_index === product.item_index ||
//                     p.item_name === product.item_name
//             );
//             if (index > -1) {
//                 this.products.splice(index, 1);
//             }
//         },
//         clearDeck(){
//             this.products = [];
//         }
//     }
// });
